import { useCallback, useEffect, useReducer, useRef } from 'react';

import { useScrollHandler } from 'hooks';
import { useAppContext } from 'context';
import { MessageData, ObjectId } from 'types';
import { Loader } from 'components/ui';
import { call } from 'utils';
import { LoadMessagesCallback } from 'hooks/useEventStateHandler';
import { Message } from 'components';
import ScrollButton from './ScrollButton';
import DateMark from './DateMark';

import styles from './Feed.module.scss';

type Props = {
  messages?: MessageData[];
  change?: Change;
  loadMessages: LoadMessagesCallback;
  onLoad?: () => void;
};

type Change = { added?: MessageData[]; removed?: MessageData[] };
type Group = { [key: ObjectId]: boolean };

type State = {
  status: 'idle' | 'init' | 'active';
  messages: MessageData[];
  groups: Group[];
};

type Action =
  | { type: 'UPDATE_MESSAGES'; payload: { messages: MessageData[]; change: Change } }
  | { type: 'MESSAGE_LOADED'; payload: ObjectId };

const initialState: State = {
  status: 'idle',
  messages: [],
  groups: [],
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'UPDATE_MESSAGES': {
      const {
        messages,
        change: { added, removed },
      } = action.payload;

      if (!messages.length) {
        return initialState;
      }

      let { groups, status } = state;

      if (added?.length) {
        const group: Group = {};
        added.forEach(({ id }) => (group[id] = false));
        groups.push(group);
      }

      if (removed?.length && groups.length) {
        removed.forEach(({ id }) => {
          for (const group of groups) {
            if (group[id] !== undefined) {
              delete group[id];
              break;
            }
          }
        });
      }

      if (status === 'idle') {
        status = 'init';
      }

      return { ...state, status, messages, groups };
    }

    case 'MESSAGE_LOADED': {
      const groups = [];

      for (const group of state.groups) {
        let ready = true;

        for (const key in group) {
          if (key === action.payload) {
            group[key] = true;
          }

          ready = ready && group[key];
        }

        if (groups.length || !ready) {
          groups.push(group);
        }
      }

      return { ...state, groups, status: groups.length ? state.status : 'active' };
    }

    default:
      return state;
  }
};

const Feed = (props: Props) => {
  const { loadMessages, onLoad: loadCallback } = props;

  const [state, dispatch] = useReducer(reducer, initialState);

  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const trailing = useRef<HTMLDivElement>();
  const leading = useRef<HTMLDivElement>();

  const { top, bottom, scrollDown } = useScrollHandler(topRef.current, bottomRef.current);
  const { status, messages, groups } = state;
  const { setOverlay } = useAppContext();

  const loading = useRef(true);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_MESSAGES',
      payload: { messages: props.messages || [], change: props.change || {} },
    });
  }, [props.messages, props.change]);

  useEffect(() => {
    if (top && !loading.current) {
      loadMessages(active => (loading.current = active));
    }
  }, [loadMessages, top]);

  useEffect(() => {
    if (status === 'active') {
      const container = containerRef.current;

      if (container) {
        container.scrollTop = container.scrollHeight;

        if (container.scrollHeight < container.offsetHeight * 2) {
          loadMessages(active => (loading.current = active));
        } else {
          loading.current = false;
        }
      }

      call(loadCallback);
    }
  }, [loadCallback, loadMessages, status]);

  const onLoad = useCallback(
    (id: ObjectId) => dispatch({ type: 'MESSAGE_LOADED', payload: id }),
    []
  );

  const setTrailing = useCallback((el: HTMLDivElement) => {
    if (trailing.current && trailing.current !== el) {
      const container = containerRef.current;

      if (container) {
        const offset = trailing.current.offsetTop - el.offsetTop;
        container.scrollTop = container.scrollTop + offset;
        loading.current = false;
      }
    }

    trailing.current = el;
  }, []);

  const setLeading = useCallback(
    (el: HTMLDivElement, bottom?: boolean) => {
      if (leading.current && leading.current !== el) {
        if (bottom) {
          scrollDown();
        }
      }

      leading.current = el;
    },
    [scrollDown]
  );

  const renderMessages = () => {
    const { trailing, leading } = findGuides(messages, groups);

    const marks = [];
    const components = [];

    for (const item of messages) {
      if (!marks.length || !item.date.isSame(marks[marks.length - 1], 'day')) {
        marks.push(item.date);
        components.push(<DateMark key={item.date.valueOf()} date={item.date} />);
      }

      components.push(
        <Message
          key={item.id}
          data={item}
          onLoad={onLoad}
          onAction={setOverlay}
          loading={isLoading(item.id, groups)}
          ref={
            item.id === trailing || item.id === leading
              ? el => {
                  if (el) {
                    if (item.id === trailing) {
                      setTrailing(el);
                    }

                    if (item.id === leading) {
                      setLeading(el, bottom);
                    }
                  }
                }
              : undefined
          }
        />
      );
    }

    return components;
  };

  const active = status === 'active';

  return (
    <div className={styles.feed}>
      <div className={styles.container} ref={containerRef}>
        <div className={styles.anchor} ref={active ? topRef : undefined}>
          <Loader size="0.6rem" type="spin" visible={active && loading.current} />
        </div>

        {renderMessages()}

        <div className={styles.anchor} ref={active ? bottomRef : undefined} />
      </div>

      <ScrollButton visible={!bottom} onClick={scrollDown} />
    </div>
  );
};

export default Feed;

const isLoading = (id: ObjectId, groups: Group[]) => {
  if (groups.length) {
    for (const group of groups) {
      if (group[id] !== undefined) {
        return true;
      }
    }
  }

  return false;
};

const findGuides = (messages: MessageData[], groups: Group[]) => {
  let trailing, leading;

  for (let i = 0; i < messages.length; ++i) {
    if (!trailing && !isLoading(messages[i].id, groups)) {
      trailing = messages[i].id;
    }

    if (!leading && !isLoading(messages[messages.length - 1 - i].id, groups)) {
      leading = messages[messages.length - 1 - i].id;
    }

    if (trailing && leading) {
      break;
    }
  }

  return { trailing, leading };
};
